/**
 * VIVANA — Sticky buy bar.
 *
 * Shows a fixed bottom bar once the product form's Add to Cart button scrolls
 * out of view, and forwards clicks to that real button.
 *
 * The one rule this file exists to enforce: it owns no cart logic. The proxy is
 * a literal `realButton.click()`, so the pack selector, Appstle DOI/subscription
 * swap, bundle-swap intercept, fly-to-cart and the variant-required guard all
 * run exactly as they do for a direct click. Nothing here duplicates them, and
 * nothing here needs to be updated when they change.
 *
 * Blocked adds are not handled here either. vivana-variant-required.js already
 * calls showMessage() + scrollToVariant() when it blocks, and it only matches
 * buttons inside the product form — which the bar's button is not — so it sees
 * the proxied click on the real button and does the right thing on its own.
 *
 * TWO INVARIANTS, both learned the hard way:
 *
 * 1. Toggling the bar must never change document layout. The bar is
 *    `position: fixed` and animates with `transform`; the space reserved at the
 *    end of the page is a static CSS constant. An earlier version measured the
 *    bar and set body padding while it was visible, which reflowed the document,
 *    moved the element the IntersectionObserver watched, and hung the page in a
 *    show/hide loop.
 *
 * 2. Mounting must be idempotent and repeatable. The theme editor re-renders a
 *    section on every settings change, and Horizon morphs the buy area on
 *    variant change, so both the bar and its anchor can be replaced at any time.
 */
(function () {
  'use strict';

  if (window.__vivanaStickyBuyInit) return;
  window.__vivanaStickyBuyInit = true;

  var BAR_SELECTOR = 'vivana-sticky-buy';
  var BUY_AREA_SELECTOR = '.buy-buttons-block';
  var REAL_BUTTON_SELECTOR = 'button[name="add"], [ref="addToCartButton"]';

  /** Live lookups, never cached: both nodes can be replaced at any time. */
  function getBar() {
    return document.querySelector(BAR_SELECTOR);
  }

  function getBuyArea() {
    return document.querySelector(BUY_AREA_SELECTOR);
  }

  function getRealButton() {
    var area = getBuyArea();
    return area ? area.querySelector(REAL_BUTTON_SELECTOR) : null;
  }

  /**
   * Hide the bar while something modal is open.
   *
   * Horizon's drawers (cart, header, account, filters) are native <dialog>
   * elements and lock the page by setting a `scroll-lock` attribute on <html>
   * (see dialog.js). The Vivana quick view is not a <dialog> and marks <body>
   * with a class instead.
   */
  function anyModalOpen() {
    return (
      document.documentElement.hasAttribute('scroll-lock') ||
      document.body.classList.contains('vivana-quickview-open')
    );
  }

  var buyAreaVisible = true;
  var isVisible = null; // null = undecided, so the first apply always runs
  var observer = null;

  /**
   * Only ever touches the bar itself — see invariant 1. Guarded on state so we
   * never write to the DOM redundantly.
   */
  function setVisible(visible) {
    var bar = getBar();
    if (!bar) return;
    if (isVisible === visible) return;
    isVisible = visible;

    if (visible) {
      bar.removeAttribute('hidden');
      bar.classList.add('vivana-sticky-buy--visible');
    } else {
      bar.classList.remove('vivana-sticky-buy--visible');
    }
  }

  function refreshVisibility() {
    setVisible(!buyAreaVisible && !anyModalOpen());
  }

  /** Mirror the real button's availability so sold out cannot be added here. */
  function syncDisabledState() {
    var bar = getBar();
    if (!bar) return;
    var cta = bar.querySelector('[data-vsb-cta]');
    if (!cta) return;
    var real = getRealButton();
    cta.disabled = real ? Boolean(real.disabled) : true;
  }

  /** The checked pack card, or null when the product has no pack selector. */
  function selectedPackCard() {
    var cards = document.querySelectorAll('.vivana-pack-card');
    for (var i = 0; i < cards.length; i++) {
      var input = cards[i].querySelector('input[type="radio"]');
      if (input && input.checked) return cards[i];
    }
    return null;
  }

  function packLabelFrom(card) {
    var titleEl = card.querySelector('.vivana-pack-card__title');
    if (!titleEl) return '';
    // The title also contains pill/badge spans; take only the direct text so
    // "3-Month Pack -15%" does not leak the promo pill into the bar.
    var text = '';
    for (var i = 0; i < titleEl.childNodes.length; i++) {
      var node = titleEl.childNodes[i];
      if (node.nodeType === 3) text += node.textContent;
    }
    return text.replace(/\s+/g, ' ').trim();
  }

  /**
   * Strip tags before displaying as text.
   *
   * `data-now="{{ ... | money }}"` holds the money filter's OUTPUT, which in this
   * shop includes markup — `<span class=money>£147.00</span>`. Assigning that to
   * textContent printed the tags literally in the bar. Parsing it and taking the
   * text is the fix; a regex would mangle the entity-escaped attribute.
   */
  function textOf(html) {
    if (!html) return '';
    if (html.indexOf('<') === -1) return html.replace(/\s+/g, ' ').trim();
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function priceFrom(card) {
    if (card) {
      // Prefer the rendered node: its textContent is already tag-free.
      var nowEl = card.querySelector('.vivana-pack-card__price-now');
      if (nowEl) {
        var rendered = nowEl.textContent.replace(/\s+/g, ' ').trim();
        if (rendered) return rendered;
      }
      var now = textOf(card.getAttribute('data-now'));
      if (now) return now;
    }
    // No pack selector on this product — fall back to the main price block.
    var main = document.querySelector('product-price .price, product-price');
    return main ? main.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function syncMeta() {
    var bar = getBar();
    if (!bar) return;

    var packEl = bar.querySelector('[data-vsb-pack]');
    var priceEl = bar.querySelector('[data-vsb-price]');

    var showPrice = bar.getAttribute('data-show-price') === 'true';
    var showPackLabel = bar.getAttribute('data-show-pack-label') === 'true';

    // Price and pack hide independently. The product name always shows, so
    // there is no wrapper to collapse — an earlier version gated everything on
    // a meta wrapper that no longer exists in the markup.
    try {
      var card = selectedPackCard();

      if (showPackLabel && packEl) {
        var label = card ? packLabelFrom(card) : '';
        if (label) {
          packEl.textContent = label;
          packEl.removeAttribute('hidden');
        } else {
          packEl.setAttribute('hidden', '');
        }
      }

      if (showPrice && priceEl) {
        var price = priceFrom(card);
        if (price) {
          priceEl.textContent = price;
          priceEl.removeAttribute('hidden');
        } else {
          priceEl.setAttribute('hidden', '');
        }
      }
    } catch (e) {
      // Mirroring is cosmetic. Never let it take the button down with it:
      // hide the two optional bits and leave the product name and CTA alone.
      if (packEl) packEl.setAttribute('hidden', '');
      if (priceEl) priceEl.setAttribute('hidden', '');
    }
  }

  function syncAll() {
    syncDisabledState();
    syncMeta();
  }

  /**
   * (Re)attach to whatever bar and buy area are currently in the DOM. Safe to
   * call repeatedly — see invariant 2.
   */
  function mount() {
    var bar = getBar();
    var buyArea = getBuyArea();
    if (!bar || !buyArea) return;

    // Reset visibility state: this may be a freshly rendered bar that starts
    // hidden, so the next refresh must be allowed to apply.
    isVisible = null;

    if (typeof IntersectionObserver === 'function') {
      if (observer) observer.disconnect();
      observer = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            buyAreaVisible = entries[i].isIntersecting;
          }
          refreshVisibility();
        },
        // A sliver of the button showing still counts as reachable. rootMargin
        // adds hysteresis so a buy area resting near the boundary cannot sit
        // there flipping the bar on and off.
        { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
      );
      observer.observe(buyArea);
    } else {
      // No IntersectionObserver — leave the bar hidden rather than guess.
      buyAreaVisible = true;
      refreshVisibility();
    }

    syncAll();
  }

  function bindOnce() {
    // Delegated, so it survives the bar being replaced.
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (!t.closest('[data-vsb-cta]')) return;

      var real = getRealButton();
      if (!real) return;
      // A plain click on the real button: same element, same listeners, same
      // capture-phase guards, same form submit. Nothing is simulated.
      real.click();
    });

    // Re-check when something modal opens or closes. Two nodes, because the two
    // mechanisms live in different places: `scroll-lock` on <html> for Horizon's
    // dialogs, a class on <body> for the quick view. Nothing in this file writes
    // to either, so this cannot feed back on itself.
    if (typeof MutationObserver === 'function') {
      var modalObserver = new MutationObserver(refreshVisibility);
      modalObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['scroll-lock'],
      });
      modalObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    // Horizon re-renders the buy area on variant change and fires this on
    // document. Pack option changes land here too, via the picker.
    document.addEventListener('variant:update', function () {
      // The buy area node was probably replaced, so re-observe it as well.
      mount();
    });

    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('.vivana-pack-options')) syncAll();
    });

    // Theme editor: every settings change re-renders the section.
    document.addEventListener('shopify:section:load', mount);
    document.addEventListener('shopify:section:unload', function () {
      if (observer) observer.disconnect();
      observer = null;
      isVisible = null;
    });
  }

  function start() {
    bindOnce();
    mount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
